import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PacksSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/shopService'

const Pack = styled(Card)`
  display: flex;
  align-items: center;
  gap: 16px;
`

const Ring = styled.div`
  --pct: ${(p) => p.$pct}%;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  flex-shrink: 0;
  background: conic-gradient(
    ${(p) => p.theme?.color?.accent || '#6B9080'} var(--pct),
    ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'} var(--pct)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    inset: 8px;
    background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
    border-radius: 50%;
  }
`

const RingText = styled.span`
  position: relative;
  z-index: 1;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

export default function PacksPage() {
  const { currentProfile } = usePatient()
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const list = await svc.listPacks(currentProfile)
      setItems(list)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setItems(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/shop">Back to Shop</BackLink>
        <div>
          <Pill>Shop</Pill>
          <PageHeading style={{ marginTop: 12 }}>Session packs</PageHeading>
          <PageSubheading>Active packs and their progress. Buying packs lands in B6.</PageSubheading>
        </div>

        {loading && !items ? (
          <PacksSkeleton />
        ) : !items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No active packs.</p>
            <Muted>Buying a pack saves money compared to one-off bookings.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {items.map((p) => {
              const pct = p.sessionsTotal ? Math.round((p.sessionsUsed / p.sessionsTotal) * 100) : 0
              return (
                <Pack key={p.packId}>
                  <Ring $pct={pct}>
                    <RingText>
                      {p.sessionsUsed}/{p.sessionsTotal}
                    </RingText>
                  </Ring>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{p.name}</div>
                    <Muted>{p.practitionerName}</Muted>
                    <Muted style={{ marginTop: 4 }}>{p.expiryLabel}</Muted>
                  </div>
                </Pack>
              )
            })}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
